import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { XCircle } from 'lucide-react';
import { LEAVE_TYPE_COLORS } from './constants';
import { formatDate, capitalizeFirst } from '../../lib/utils';

export const CancelLeaveModal = ({
  open,
  onOpenChange,
  leaveInfo,
  cancellationReason,
  setCancellationReason,
  cancelling,
  onCancel,
  getEmployeeName
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <XCircle className="w-5 h-5" />
            Cancel Leave
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this leave? If approved, the leave balance will be restored.
          </DialogDescription>
        </DialogHeader>

        {leaveInfo && (
          <div className="py-4 space-y-4">
            {/* Leave Details */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Employee</span>
                <span className="text-sm font-medium">
                  {leaveInfo.employee_name || getEmployeeName(leaveInfo.employee_id)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Leave Type</span>
                <Badge variant="outline" className={LEAVE_TYPE_COLORS[leaveInfo.leave_type]?.text}>
                  {capitalizeFirst(leaveInfo.leave_type)}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Duration</span>
                <span className="text-sm font-medium">{leaveInfo.days} day(s)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Dates</span>
                <span className="text-sm font-medium">
                  {formatDate(leaveInfo.start_date)} - {formatDate(leaveInfo.end_date)}
                </span>
              </div>
            </div>

            {/* Cancellation Reason */}
            <div className="space-y-2">
              <Label htmlFor="cancellation-reason">Cancellation Reason *</Label>
              <Textarea
                id="cancellation-reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Enter reason for cancellation..."
                rows={3}
                data-testid="cancellation-reason-input"
              />
              <p className="text-xs text-slate-500">
                This reason is required and will be visible to the employee.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={cancelling}
          >
            Keep Leave
          </Button>
          <Button 
            variant="destructive"
            onClick={onCancel}
            disabled={cancelling}
            className="gap-2"
            data-testid="confirm-cancel-leave-btn"
          >
            {cancelling ? (
              <>
                <div className="spinner w-4 h-4"></div>
                Cancelling...
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4" />
                Cancel Leave
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CancelLeaveModal;
